import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Alert,
  Spinner,
  Badge,
} from "reactstrap";
import logo from "../../assets/img/icon-transparent.png";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { generateCheckoutID } from "actions/quotationReview";
import axios from "axios";
import { WORK_ORDER } from "components/Common/constants";

// Custom RightPanelCard component
const RightPanelCard = ({
  isOpen,
  onClose,
  title,
  children,
  width = "500px",
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1040,
          transition: "all 0.3s ease",
        }}
        // onClick={onClose}
      />

      {/* Right Panel Card */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: width,
          backgroundColor: "white",
          boxShadow: "-2px 0 8px rgba(0, 0, 0, 0.15)",
          zIndex: 1050,
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s ease-in-out",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.25rem 1.5rem",
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: "#0ea5e9",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "white",
              fontSize: "1.5rem",
              cursor: "pointer",
              padding: 0,
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "1.5rem",
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
};

const PaymentConfirmScreen = ({ paymentDetails, onConfirm, onCancel }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  let quotationData = state?.quotationData;
  let uuid = state?.uuid;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCheckoutPanel, setShowCheckoutPanel] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Sample payment data - replace with your actual props
  const paymentData = paymentDetails || {
    amount: 1595.05,
    currency: "SAR",
    quotationId: "QTN-2024-001",
    customerName: "John Doe",
    customerEmail: "john.doe@example.com",
    paymentMethod: "Credit Card / MADA",
    dueDate: "2024-12-31",
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-SA", {
      style: "currency",
      currency: quotationData?.currency || "SAR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const showAlertMessage = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false);
    }, 5000);
  };

  const handleConfirmPayment = () => {
    // setShowConfirmModal(true);
    setShowCheckoutPanel(true);
  };

  const handleMakePayment = () => {
    setShowConfirmModal(false);
    setShowCheckoutPanel(true);
  };

  const handleCheckoutComplete = (paymentResult) => {
    setShowCheckoutPanel(false);
    setProcessing(true);

    // Simulate payment processing
    setTimeout(() => {
      setPaymentStatus("success");
      showAlertMessage("Payment processed successfully!", "success");

      if (onConfirm) {
        onConfirm({ success: true, transactionId: "TXN_" + Date.now() });
      }
      setProcessing(false);
    }, 1000);
  };

  const handleCheckoutError = (error) => {
    setShowCheckoutPanel(false);
    setPaymentStatus("error");
    showAlertMessage(
      error?.message || "Payment failed. Please try again.",
      "danger",
    );

    if (onConfirm) {
      onConfirm({ success: false, error: error?.message });
    }
  };

  const cancelPayment = () => {
    setShowConfirmModal(false);
    setTimeout(() => {
      navigate(`/review-quotation/${uuid}`);
    }, 1500);
  };

  const CheckoutContent = ({
    onSuccess,
    onError,
    quotationData: quoteData,
  }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [checkoutId, setCheckoutId] = useState(null);
    const [widgetURL, setWidgetURL] = useState("");
    const [showPaymentIframe, setShowPaymentIframe] = useState(false);
    const [iframeLoading, setIframeLoading] = useState(true);
    const iframeRef = React.useRef(null);

    // Function to get checkout ID from your backend
    const getCheckoutId = async () => {
      try {
        let payload = {
          uuid: uuid,
          quotationID: quotationData?.ID,
          payableAmount: quotationData?.payableAmount,
          returnUrl: `${window.location.origin}${window.location.pathname}`,
        };
        const res = await generateCheckoutID(payload);
        if (res?.code === 200) {
          return {
            checkoutID: res?.data?.checkoutID,
            widgetURL: res?.data?.widgetURL,
          };
        }
      } catch (error) {
        console.error("Error getting checkout ID:", error);
        throw error;
      }
    };

    // Create HTML content for iframe with proper HyperPay widget
    const createIframeContent = (checkoutId, widgetURL) => {
      return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              background: #ffffff;
              padding: 20px;
            }

            .payment-container {
              max-width: 500px;
              margin: 0 auto;
            }

            .payment-header {
              text-align: center;
              margin-bottom: 24px;
            }

            .payment-header h3 {
              color: #1f2937;
              font-size: 20px;
              margin-bottom: 8px;
            }

            .payment-header p {
              color: #6b7280;
              font-size: 14px;
            }

            .secure-badge {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              background: #f0fdf4;
              color: #166534;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 12px;
              margin-top: 12px;
            }

            .payment-summary {
              background: #f9fafb;
              border-radius: 12px;
              padding: 16px;
              margin-bottom: 24px;
              border: 1px solid #e5e7eb;
            }

            .summary-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
            }

            .summary-row:last-child {
              margin-bottom: 0;
              padding-top: 8px;
              border-top: 1px solid #e5e7eb;
            }

            .summary-label {
              color: #6b7280;
              font-size: 14px;
            }

            .summary-value {
              color: #1f2937;
              font-weight: 500;
              font-size: 14px;
            }

            .total-amount {
              font-size: 18px;
              font-weight: bold;
              color: #2563eb;
            }

            .payment-widget-wrapper {
              margin-top: 20px;
            }

            .loading-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(255, 255, 255, 0.9);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 1000;
            }

            .spinner {
              width: 40px;
              height: 40px;
              border: 3px solid #e5e7eb;
              border-top-color: #2563eb;
              border-radius: 50%;
              animation: spin 0.8s linear infinite;
            }
  @keyframes spinSmooth {
        to { transform: rotate(360deg); }
      }

        @keyframes pulseFade {
        0% { opacity: 0.6; }
        50% { opacity: 1; }
        100% { opacity: 0.6; }
      }

            .footer-note {
              text-align: center;
              margin-top: 20px;
              font-size: 11px;
              color: #9ca3af;
            }
          </style>
          <script src="${widgetURL}"></script>
        </head>
        <body>
          <div class="payment-container">
            <div class="payment-header">
              <h3>Secure Payment</h3>
              <p>Enter your card details to complete payment</p>
            </div>

            <div class="payment-summary">
              <div class="summary-row">
                <span class="summary-label">Amount to Pay</span>
                <span class="summary-value total-amount">${quoteData?.currency || "SAR"} ${parseFloat(quoteData?.payableAmount || 0).toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Merchant</span>
                <span class="summary-value">${quoteData?.merchantName || "Falcon Kitchens"}</span>
              </div>
            </div>

            <div class="payment-widget-wrapper">
              <form class="paymentWidgets" data-brands="MADA VISA MASTER AMEX"></form>
            </div>

            <div class="footer-note">
              Payment secured by HyperPay
            </div>
          </div>

          <script>
            // Configure payment widget
            window.wpwlOptions = {
              style: "plain",
              locale: "en",
              maskCvv: true,
              onReady: function() {
                // Notify parent that widget is ready
                window.parent.postMessage({
                  type: 'widget-ready',
                  checkoutId: '${checkoutId}'
                }, '*');
              },
              onSuccess: function(result) {
                // Payment successful
                window.parent.postMessage({
                  type: 'payment-success',
                  result: result,
                  transactionId: result.id,
                  amount: '${quoteData?.payableAmount}',
                  currency: '${quoteData?.currency}'
                }, '*');
              },
              onError: function(error) {
                // Payment failed
                window.parent.postMessage({
                  type: 'payment-error',
                  error: error,
                  message: error.message || 'Payment processing failed'
                }, '*');
              },
              onCancel: function() {
                // User cancelled
                window.parent.postMessage({
                  type: 'payment-cancel'
                }, '*');
              }
            };

            // Hide loading overlay when widget is ready
            window.addEventListener('load', function() {
              setTimeout(function() {
                const loadingOverlay = document.querySelector('.loading-overlay');
                if (loadingOverlay) {
                  loadingOverlay.style.display = 'none';
                }
              }, 500);
            });
          </script>
        </body>
      </html>
    `;
    };

    // Handle messages from iframe (postMessage)
    React.useEffect(() => {
      const handleMessage = (event) => {
        // Verify origin for security
        const allowedOrigins = [
          "https://eu-test.oppwa.com",
          "https://oppwa.com",
          window.location.origin,
        ];

        if (!allowedOrigins.includes(event.origin)) {
          return;
        }

        // Handle payment success/failure messages from iframe
        if (event.data && event.data.type) {
          switch (event.data.type) {
            case "widget-ready":
              setIframeLoading(false);
              break;

            case "payment-success":
              onSuccess({
                success: true,
                transactionId:
                  event.data.transactionId ||
                  event.data.result?.id ||
                  "TXN_" + Date.now(),
                amount: quoteData?.payableAmount,
                currency: quoteData?.currency,
              });
              setShowPaymentIframe(false);
              break;

            case "payment-error":
              onError({
                message:
                  event.data.message || "Payment failed. Please try again.",
              });
              setShowPaymentIframe(false);
              break;

            case "payment-cancel":
              onError({ message: "Payment cancelled by user" });
              setShowPaymentIframe(false);
              break;

            default:
              break;
          }
        }
      };

      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }, [onSuccess, onError, quoteData]);

    const initializePayment = async () => {
      setIsLoading(true);
      try {
        const response = await getCheckoutId();
        if (response) {
          setCheckoutId(response?.checkoutID);
          setWidgetURL(response?.widgetURL);
          setShowPaymentIframe(true);
          setIframeLoading(true);
        } else {
          throw new Error("Failed to get checkout ID");
        }
      } catch (error) {
        console.error("Payment initialization error:", error);
        onError({ message: "Failed to initialize payment. Please try again." });
      } finally {
        setIsLoading(false);
      }
    };

    const closeIframe = () => {
      setShowPaymentIframe(false);
      // setPaymentUrl(null);
      setCheckoutId(null);
      setIframeLoading(true);
    };

    // Format currency helper
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: quoteData?.currency || "SAR",
        minimumFractionDigits: 2,
      }).format(amount);
    };

    // Show loading state while initializing
    if (isLoading) {
      return (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid #e5e7eb",
              borderTopColor: "#2563eb",
              borderRadius: "50%",
              margin: "0 auto",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
          <p style={{ marginTop: "20px", color: "#666" }}>
            Initializing secure payment gateway...
          </p>
        </div>
      );
    }

    // Show payment iframe with proper HTML content
    if (showPaymentIframe && checkoutId && widgetURL) {
      const iframeContent = createIframeContent(checkoutId, widgetURL);

      return (
        <div>
          <div
            style={{
              marginBottom: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 style={{ color: "#1f2937", margin: 0, fontSize: "18px" }}>
              {/* Secure 3D Secure Payment */}
            </h3>
            <button
              onClick={closeIframe}
              style={{
                background: "#f3f4f6",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                cursor: "pointer",
                fontSize: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e5e7eb";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f3f4f6";
              }}
            >
              ×
            </button>
          </div>

          {/* Iframe Container */}
          <div
            style={{
              position: "relative",
              width: "100%",
              minHeight: "750px",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#ffffff",
            }}
          >
            {iframeLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  zIndex: 10,
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "3px solid #e5e7eb",
                    borderTopColor: "#2563eb",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                <style>{`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
                <p style={{ marginTop: "20px", color: "#666" }}>
                  Loading secure payment form...
                </p>
              </div>
            )}

            <iframe
              ref={iframeRef}
              srcDoc={iframeContent}
              title="Secure Payment Gateway"
              style={{
                width: "100%",
                minHeight: "750px",
                border: "none",
                display: "block",
              }}
              onLoad={() => {
                // Iframe loaded, but widget might still be initializing
                setTimeout(() => {
                  setIframeLoading(false);
                }, 1000);
              }}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-top-navigation"
            />
          </div>

          <div
            style={{
              marginTop: "16px",
              textAlign: "center",
              fontSize: "12px",
              color: "#9ca3af",
            }}
          >
            <span>🔒 Payment secured by HyperPay | 3D Secure Enabled</span>
          </div>
        </div>
      );
    }

    // Initial view with Proceed button
    return (
      <div>
        <div style={{ marginBottom: "24px", textAlign: "center" }}>
          <h3 style={{ color: "#1f2937", marginBottom: "8px" }}>Checkout</h3>
          <p style={{ color: "#6b7280", fontSize: "14px" }}>
            Please review your payment details below
          </p>
        </div>

        {/* Payment Summary */}
        <div
          style={{
            background: "#f9fafb",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
            border: "1px solid #e5e7eb",
          }}
        >
          <h4
            style={{ fontSize: "16px", marginBottom: "16px", color: "#1f2937" }}
          >
            Payment Summary
          </h4>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <span style={{ color: "#6b7280" }}>Quotation ID:</span>
            <span style={{ fontWeight: "500", color: "#1f2937" }}>
              {quoteData?.ID}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <span style={{ color: "#6b7280" }}>{WORK_ORDER} NO:</span>
            <span style={{ fontWeight: "500", color: "#1f2937" }}>
              {quoteData?.jobID}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingTop: "12px",
              marginTop: "4px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <span style={{ fontWeight: "600", color: "#374151" }}>
              Total Amount:
            </span>
            <span
              style={{ fontWeight: "bold", color: "#2563eb", fontSize: "20px" }}
            >
              {formatCurrency(quoteData?.payableAmount)}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
          <button
            onClick={() => onError({ message: "Payment cancelled" })}
            style={{
              flex: 1,
              padding: "10px 20px",
              backgroundColor: "#f3f4f6",
              color: "#374151",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e5e7eb";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#f3f4f6";
            }}
          >
            Cancel
          </button>
          <button
            onClick={initializePayment}
            style={{
              flex: 1,
              padding: "10px 20px",
              backgroundColor: "#0ea5e9",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#0ea5e9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#0ea5e9";
            }}
          >
            Proceed to Secure Payment
          </button>
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="payment-container">
      <Col lg={6} md={8} sm={10} xs={12}>
        <Card className="payment-card shadow border-0">
          <CardBody className="p-0">
            {/* Header with Logo */}
            <div className="payment-header text-center">
              <div className="logo-wrapper">
                <img src={logo} alt="Company Logo" className="payment-logo" />
              </div>
              <h2 className="payment-title mb-0">Payment Confirmation</h2>
              {paymentStatus && (
                <Badge
                  color={paymentStatus === "success" ? "success" : "danger"}
                  pill
                  className="mt-3 px-3 py-2"
                >
                  {paymentStatus === "success"
                    ? "PAYMENT SUCCESSFUL"
                    : "PAYMENT FAILED"}
                </Badge>
              )}
            </div>

            {/* Alert Message */}
            {showAlert && (
              <Alert
                color={alertType}
                className="mx-3 mt-3 mb-0"
                toggle={() => setShowAlert(false)}
              >
                {alertMessage}
              </Alert>
            )}

            {/* Payment Details */}
            <div className="payment-details">
              <div className="amount-section text-center mb-3 pb-2 border-bottom">
                <p className="amount-label mb-1">Total Amount to Pay</p>
                <h1 className="amount-value fw-bold mb-0">
                  {formatCurrency(quotationData?.payableAmount)}
                </h1>
              </div>

              <div className="details-section">
                <h5 className="section-title mb-2">Payment Summary</h5>

                <div className="detail-row d-flex justify-content-between border-bottom">
                  <span className="detail-label">Quotation ID:</span>
                  <span className="detail-value">{quotationData?.ID}</span>
                </div>
                <div className="detail-row d-flex justify-content-between border-bottom">
                  <span className="detail-label">{WORK_ORDER} NO:</span>
                  <span className="detail-value">{quotationData?.jobID}</span>
                </div>
              </div>

              {/* Total Amount */}
              <div className="total-section mt-3 pt-2 border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="total-label">TOTAL PAYABLE:</span>
                  <span className="total-value">
                    {formatCurrency(quotationData?.payableAmount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {!paymentStatus && (
              <div className="payment-actions">
                <Button
                  block
                  onClick={handleConfirmPayment}
                  disabled={processing}
                  className="pay-now-btn fw-bold"
                >
                  {processing ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    "Confirm & Pay"
                  )}
                </Button>

                <Button
                  color="link"
                  block
                  onClick={cancelPayment}
                  disabled={processing}
                  className="cancel-btn mt-2"
                >
                  Cancel Payment
                </Button>
              </div>
            )}

            {/* Success/Error Actions */}
            {paymentStatus === "success" && (
              <div className="payment-actions">
                <Button
                  color="success"
                  block
                  onClick={() => (window.location.href = "/dashboard")}
                  className="py-2 fw-bold"
                  style={{ borderRadius: "0.5rem" }}
                >
                  Go to Dashboard
                </Button>
              </div>
            )}

            {paymentStatus === "error" && (
              <div className="payment-actions">
                <Button
                  block
                  onClick={() => window.location.reload()}
                  className="py-2 fw-bold"
                  style={{
                    backgroundColor: "#0ea5e9",
                    borderColor: "#0ea5e9",
                    borderRadius: "0.5rem",
                  }}
                >
                  Try Again
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        toggle={() => setShowConfirmModal(false)}
      >
        <ModalBody>
          <div className="text-center">
            <div className="mb-2">
              <h4 className="fw-bold" style={{ color: "#0ea5e9" }}>
                {formatCurrency(paymentData.amount)}
              </h4>
            </div>
            <p className="mb-0">
              Are you sure you want to proceed with this payment?
            </p>
            <small className="text-muted">This action cannot be undone.</small>
          </div>
        </ModalBody>
        <ModalFooter
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <Button
            color="secondary"
            onClick={() => setShowConfirmModal(false)}
            style={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            color="success"
            onClick={handleMakePayment}
            style={{
              backgroundColor: "#0ea5e9",
              borderColor: "#0ea5e9",
              flex: 2,
            }}
          >
            Make Payment
          </Button>
        </ModalFooter>
      </Modal>

      {/* RightPanelCard for Checkout */}
      <RightPanelCard
        isOpen={showCheckoutPanel}
        onClose={() => setShowCheckoutPanel(false)}
        // title="Secure Checkout"
        width="625px"
      >
        <CheckoutContent
          onSuccess={handleCheckoutComplete}
          onError={handleCheckoutError}
          quotationData={quotationData}
        />
      </RightPanelCard>
    </Container>
  );
};

export default PaymentConfirmScreen;
